//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import qs from 'query-string';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FocusTrap from 'focus-trap-react';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Modal, Badge, Button, ButtonStyle } from '@cmp/common';
import { BasicInfo, Products, CheckoutDetails } from '../../common';
//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import { Details as DetailsRequest } from '@api/endpoints/get/recommendation';
//------------------------------------------------------------------------------
// Helpers & Constants ---------------------------------------------------------
import { referralFormatter } from '@helpers/formatter';
import { ReferralStatus } from '@helpers/constants/referral';
import { withTranslation } from 'react-i18next';
import { StoreConfigContext } from '@helpers/storeConfigContext';
import { handleGtmEvent } from '@helpers/gtm';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class Details extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      id: '',
    };
    this.mountTrap = this.mountTrap.bind(this);
    this.unmountTrap = this.unmountTrap.bind(this);
  }

  componentDidMount() {
    const { history, match: { params: { id } = {} } = {} } = this.props;

    if (!id) return history.replace('/recommendations');
    this.setState({ id });
    this.mountTrap();
  }

  mountTrap() {
    this.setState({ activeTrap: true });
  }

  unmountTrap() {
    this.setState({ activeTrap: false });
  }

  droppedProductsComponent(purchaseProducts, droppedProducts) {
    const { t } = this.props;
    if (!droppedProducts || droppedProducts.length <= 0) return '';

    const componentClasses = classNames(styles.DroppedProducts, {
      [styles.noMargin]: !purchaseProducts || purchaseProducts.length <= 0,
    });

    return (
      <div className={componentClasses}>
        <span className={styles.Title}>
          {t('dashboard_ItemsWerentPurchased')}
        </span>
        <Products products={droppedProducts} />
      </div>
    );
  }

  isValid(id) {
    const blacklist = ['professional-code'];
    if (!id || blacklist.includes(id)) {
      return false;
    }
    return true;
  }

  render() {
    const { id } = this.state;
    const { location, history, t } = this.props;
    const {
      storeConfig: { language },
    } = this.context;

    const componentClasses = classNames(styles.Details);

    return (
      <FocusTrap>
        <div>
          <div id="recDetailModal" tabIndex="0">
            <Modal.Base
              className={componentClasses}
              onClose={() => history.push(location.state.from)}
            >
              <DetailsRequest pathParams={{ id }} skip={!this.isValid(id)}>
                {({ loading, error, data }) => {
                  if (!data) return '';

                  const recommendationTitle = data.consumerName
                    ? t('dashboard_RecommendationFor', {
                        name: data.consumerName,
                      })
                    : t('dashboard_Recommendation');
                  const additionalPurchaseTitle = data.consumerName
                    ? t('dashboard_PurchasedBy', { name: data.consumerName })
                    : t('dashboard_Purchase');
                  const purchaseProducts =
                    referralFormatter.purchaseProducts(data);
                  const droppedProducts =
                    referralFormatter.droppedProducts(data);

                  const purchaseDetails =
                    language === 'ko' ? (
                      <span>
                        {referralFormatter.formattedCreatedAt(data, {
                          locale: language,
                        })}
                        {referralFormatter.status(data) ===
                        ReferralStatus.AdditionalPurchase
                          ? t('dashboard_Purchased')
                          : t('dashboard_Recommended')}{' '}
                        {t('dashboard_On')}{' '}
                      </span>
                    ) : (
                      <span>
                        {referralFormatter.status(data) ===
                        ReferralStatus.AdditionalPurchase
                          ? t('dashboard_Purchased')
                          : t('dashboard_Recommended')}{' '}
                        {t('dashboard_On')}{' '}
                        {referralFormatter.formattedCreatedAt(data, {
                          locale: language,
                        })}
                      </span>
                    );

                  return (
                    <>
                      <Helmet>
                        <title>
                          {referralFormatter.status(data) ===
                          ReferralStatus.AdditionalPurchase
                            ? additionalPurchaseTitle
                            : recommendationTitle}
                        </title>
                      </Helmet>

                      <BasicInfo
                        title={
                          referralFormatter.status(data) ===
                          ReferralStatus.AdditionalPurchase
                            ? additionalPurchaseTitle
                            : recommendationTitle
                        }
                        subtitleComponent={
                          <span className={styles.DetailSubtitle}>
                            {purchaseDetails}
                            <br />
                            {data.consumerEmail}
                            {data.consumerEmail && data.consumerPhone && ` - `}
                            {data.consumerPhone}
                          </span>
                        }
                        status={referralFormatter.status(data)}
                        className={styles.BasicInfo}
                      />
                      <Products products={purchaseProducts} />
                      {this.droppedProductsComponent(
                        purchaseProducts,
                        droppedProducts
                      )}
                      <CheckoutDetails
                        referral={data}
                        action={t('dashboard_RecommendAgain')}
                        onClick={() => {
                          history.push({
                            pathname: '/recommendation/patient',
                            state: {
                              fromReferral: data,
                              from: location.state.from,
                              modal: true,
                            },
                          });

                          handleGtmEvent('begin_checkout', {
                            products: items,
                            context: this.context,
                          });
                        }}
                      />
                    </>
                  );
                }}
              </DetailsRequest>
            </Modal.Base>
          </div>
        </div>
      </FocusTrap>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withTranslation()(Details);
Details.contextType = StoreConfigContext;
